import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  adminFirestoreBrowserService,
  FirestoreCollectionInfo,
  FirestoreDocumentListItem,
  FirestoreDocumentDetail,
} from '../../services/adminFirestoreBrowserService';

type ViewKind = 'root' | 'collection' | 'document';

function pathToSegments(path: string): string[] {
  return path.split('/').filter(Boolean);
}

function segmentsToPath(segments: string[]): string {
  return segments.join('/');
}

function isCollectionPath(segments: string[]): boolean {
  return segments.length % 2 === 1;
}

function matchesSearch(text: string, query: string): boolean {
  if (!query) return true;
  return text.toLowerCase().includes(query);
}

function normalizePathInput(raw: string): string {
  return raw.trim().replace(/^\/+|\/+$/g, '');
}

const AdminFirebaseDatabase: React.FC = () => {
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();

  const [segments, setSegments] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [collections, setCollections] = useState<FirestoreCollectionInfo[]>([]);
  const [documents, setDocuments] = useState<FirestoreDocumentListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  const [subcollections, setSubcollections] = useState<FirestoreCollectionInfo[]>([]);
  const [documentDetail, setDocumentDetail] = useState<FirestoreDocumentDetail | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<'document' | 'collection' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FirestoreDocumentListItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const path = segmentsToPath(segments);
  const viewKind: ViewKind = useMemo(() => {
    if (segments.length === 0) return 'root';
    return isCollectionPath(segments) ? 'collection' : 'document';
  }, [segments]);

  const cardClass = isDark
    ? 'bg-gray-800 border-gray-700 text-gray-100'
    : 'bg-white border-gray-200 text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-500';
  const rowHover = isDark ? 'hover:bg-gray-700/60' : 'hover:bg-gray-50';
  const inputClass = isDark
    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';

  const loadCurrentView = useCallback(async () => {
    setLoading(true);
    setDeleteConfirm('');
    setDeleteTarget(null);
    try {
      if (viewKind === 'root') {
        const result = await adminFirestoreBrowserService.listRootCollections();
        setCollections(result.collections);
        setDocuments([]);
        setSubcollections([]);
        setDocumentDetail(null);
        setHasMore(false);
        setLastDocId(null);
        return;
      }

      if (viewKind === 'collection') {
        const result = await adminFirestoreBrowserService.listDocuments(path, { pageSize: 50 });
        setCollections([]);
        setDocuments(result.documents);
        setHasMore(result.hasMore);
        setLastDocId(result.lastId);
        setSubcollections([]);
        setDocumentDetail(null);
        return;
      }

      const [docResult, subResult] = await Promise.all([
        adminFirestoreBrowserService.getDocument(path),
        adminFirestoreBrowserService.listSubcollections(path),
      ]);
      setDocumentDetail(docResult);
      setSubcollections(subResult.subcollections);
      setCollections([]);
      setDocuments([]);
      setHasMore(false);
      setLastDocId(null);
    } catch (err) {
      console.error('Firestore browser load error:', err);
      const message = err instanceof Error ? err.message : 'Failed to load Firestore data';
      showError(message);
    } finally {
      setLoading(false);
    }
  }, [path, viewKind, showError]);

  useEffect(() => {
    loadCurrentView();
  }, [loadCurrentView]);

  const loadMoreDocuments = async () => {
    if (!lastDocId || viewKind !== 'collection') return;
    try {
      const result = await adminFirestoreBrowserService.listDocuments(path, {
        pageSize: 50,
        startAfterId: lastDocId,
      });
      setDocuments((prev) => [...prev, ...result.documents]);
      setHasMore(result.hasMore);
      setLastDocId(result.lastId);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load more documents');
    }
  };

  const navigateToSegments = (next: string[]) => {
    setSearchQuery('');
    setSearchResults([]);
    setSegments(next);
  };

  const searchTerm = searchQuery.trim().toLowerCase();
  const isPathSearch = searchQuery.trim().includes('/');

  const filteredCollections = useMemo(
    () =>
      collections.filter(
        (col) =>
          matchesSearch(col.id, searchTerm) || matchesSearch(col.path, searchTerm)
      ),
    [collections, searchTerm]
  );

  const useServerDocSearch = viewKind === 'collection' && Boolean(searchTerm) && !isPathSearch;

  const filteredDocuments = useMemo(() => {
    const source = useServerDocSearch ? searchResults : documents;
    if (!searchTerm) return source;
    return source.filter(
      (doc) =>
        matchesSearch(doc.id, searchTerm) ||
        matchesSearch(doc.path, searchTerm) ||
        matchesSearch(doc.preview, searchTerm)
    );
  }, [documents, searchResults, searchTerm, useServerDocSearch]);

  const filteredSubcollections = useMemo(
    () =>
      subcollections.filter(
        (sub) =>
          matchesSearch(sub.id, searchTerm) || matchesSearch(sub.path, searchTerm)
      ),
    [subcollections, searchTerm]
  );

  const filteredDocumentJson = useMemo(() => {
    if (!documentDetail) return '';
    const json = JSON.stringify(documentDetail.data, null, 2);
    if (!searchTerm) return json;
    return json
      .split('\n')
      .filter((line) => line.toLowerCase().includes(searchTerm))
      .join('\n');
  }, [documentDetail, searchTerm]);

  useEffect(() => {
    if (viewKind !== 'collection' || !searchTerm || isPathSearch) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await adminFirestoreBrowserService.searchDocuments(path, searchQuery.trim(), 50);
        setSearchResults(result.documents);
      } catch (err) {
        console.error('Document search error:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timer);
  }, [viewKind, path, searchQuery, searchTerm, isPathSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalized = normalizePathInput(searchQuery);
    if (!normalized) return;
    if (normalized.includes('/')) {
      navigateToPath(normalized);
      return;
    }
    if (viewKind === 'root') {
      const exact = collections.find((col) => col.id.toLowerCase() === normalized.toLowerCase());
      if (exact) navigateToPath(exact.path);
    }
  };

  const navigateToPath = (nextPath: string) => {
    navigateToSegments(pathToSegments(nextPath));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const expectedPath = path;
    if (deleteConfirm.trim() !== expectedPath) {
      showError(`Type "${expectedPath}" exactly to confirm.`);
      return;
    }
    setDeleting(true);
    try {
      if (deleteTarget === 'document') {
        const result = await adminFirestoreBrowserService.deleteDocument(path, deleteConfirm.trim());
        showSuccess(`Deleted document (${result.deletedDocuments} docs including subcollections).`);
        const parentSegments = segments.slice(0, -1);
        navigateToSegments(parentSegments);
        return;
      }
      const result = await adminFirestoreBrowserService.deleteCollection(path, deleteConfirm.trim());
      showSuccess(`Deleted collection (${result.deletedDocuments} documents removed).`);
      const parentSegments = segments.slice(0, -2);
      navigateToSegments(parentSegments.length > 0 ? parentSegments : []);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
      setDeleteConfirm('');
      setDeleteTarget(null);
    }
  };

  const breadcrumb = (
    <nav className={`flex flex-wrap items-center gap-1 text-sm ${mutedClass}`}>
      <button
        type="button"
        onClick={() => navigateToSegments([])}
        className={`font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
      >
        Root
      </button>
      {segments.map((segment, index) => {
        const partial = segments.slice(0, index + 1);
        const partialPath = segmentsToPath(partial);
        const isLast = index === segments.length - 1;
        return (
          <React.Fragment key={partialPath}>
            <span>/</span>
            {isLast ? (
              <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{segment}</span>
            ) : (
              <button
                type="button"
                onClick={() => navigateToSegments(partial)}
                className={`font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
              >
                {segment}
              </button>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className={`rounded-xl border p-5 ${cardClass}`}>
        <h2 className="text-lg font-semibold">Firebase Database</h2>
        <p className={`mt-1 text-sm ${mutedClass}`}>
          Browse and delete Firestore collections and documents. Deletions are permanent and include
          nested subcollections. Deploy the <code className="text-xs">adminFirestoreBrowser</code> Cloud
          Function before use.
        </p>
        <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${isDark ? 'border-amber-700/50 bg-amber-900/20 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
          Destructive actions require typing the full path to confirm. Large collections may take
          several minutes to delete.
        </div>
      </div>

      <div className={`rounded-xl border p-4 ${cardClass}`}>
        <form onSubmit={handleSearchSubmit} className="mb-4 space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  viewKind === 'root'
                    ? 'Filter collections or enter path (e.g. users/abc123)'
                    : viewKind === 'collection'
                      ? 'Filter documents, search by ID, or enter full path'
                      : 'Filter subcollections or document JSON'
                }
                className={`block w-full pl-9 pr-9 py-2 rounded-lg border text-sm ${inputClass}`}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {isPathSearch && (
              <button
                type="submit"
                className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap"
              >
                Go to path
              </button>
            )}
          </div>
          <p className={`text-xs ${mutedClass}`}>
            {viewKind === 'collection' && searchTerm && !isPathSearch
              ? searchLoading
                ? 'Searching documents by ID…'
                : `Showing matches in this collection${searchResults.length > 0 ? ` (${searchResults.length} found)` : ''}.`
              : 'Type to filter the current view. Use a path with slashes and press Enter or Go to jump directly.'}
          </p>
        </form>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {breadcrumb}
          {viewKind !== 'root' && (
            <button
              type="button"
              onClick={() => {
                if (segments.length <= 1) navigateToSegments([]);
                else navigateToSegments(segments.slice(0, -1));
              }}
              className={`text-sm px-3 py-1.5 rounded-lg border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
            >
              ← Back
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
          </div>
        ) : (
          <>
            {viewKind === 'root' && (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredCollections.map((col) => (
                  <li key={col.path}>
                    <button
                      type="button"
                      onClick={() => navigateToPath(col.path)}
                      className={`w-full text-left px-3 py-3 flex items-center justify-between ${rowHover}`}
                    >
                      <span className="font-mono text-sm">{col.id}</span>
                      <span className={`text-xs ${mutedClass}`}>collection →</span>
                    </button>
                  </li>
                ))}
                {collections.length === 0 && (
                  <p className={`py-8 text-center text-sm ${mutedClass}`}>No collections found.</p>
                )}
                {collections.length > 0 && filteredCollections.length === 0 && (
                  <p className={`py-8 text-center text-sm ${mutedClass}`}>No collections match your search.</p>
                )}
              </ul>
            )}

            {viewKind === 'collection' && (
              <>
                <div className="flex justify-end mb-3">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget('collection')}
                    className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete entire collection
                  </button>
                </div>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredDocuments.map((doc) => (
                    <li key={doc.path}>
                      <button
                        type="button"
                        onClick={() => navigateToPath(doc.path)}
                        className={`w-full text-left px-3 py-3 ${rowHover}`}
                      >
                        <div className="font-mono text-sm">{doc.id}</div>
                        {doc.preview && (
                          <div className={`mt-0.5 text-xs truncate ${mutedClass}`}>{doc.preview}</div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                {documents.length === 0 && !searchTerm && (
                  <p className={`py-8 text-center text-sm ${mutedClass}`}>This collection has no documents.</p>
                )}
                {searchLoading && searchTerm && (
                  <p className={`py-4 text-center text-sm ${mutedClass}`}>Searching…</p>
                )}
                {!searchLoading && (documents.length > 0 || useServerDocSearch) && filteredDocuments.length === 0 && searchTerm && (
                  <p className={`py-8 text-center text-sm ${mutedClass}`}>No documents match your search.</p>
                )}
                {hasMore && !searchTerm && (
                  <div className="mt-4 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMoreDocuments}
                      className={`text-sm px-4 py-2 rounded-lg border ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}

            {viewKind === 'document' && documentDetail && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => setDeleteTarget('document')}
                    className="text-sm px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete document
                  </button>
                </div>

                {subcollections.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium mb-2">Subcollections</h3>
                    <ul className={`rounded-lg border divide-y ${isDark ? 'divide-gray-700 border-gray-700' : 'divide-gray-200 border-gray-200'}`}>
                      {filteredSubcollections.map((sub) => (
                        <li key={sub.path}>
                          <button
                            type="button"
                            onClick={() => navigateToPath(sub.path)}
                            className={`w-full text-left px-3 py-2.5 flex items-center justify-between font-mono text-sm ${rowHover}`}
                          >
                            {sub.id}
                            <span className={`text-xs ${mutedClass}`}>→</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {searchTerm && filteredSubcollections.length === 0 && (
                      <p className={`mt-2 text-xs ${mutedClass}`}>No subcollections match your search.</p>
                    )}
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium mb-2">Document data</h3>
                  <pre
                    className={`text-xs overflow-auto max-h-[480px] rounded-lg border p-4 font-mono ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
                  >
                    {searchTerm
                      ? filteredDocumentJson || '(no matching fields in document JSON)'
                      : JSON.stringify(documentDetail.data, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {deleteTarget && (
        <div className={`rounded-xl border p-5 ${cardClass}`}>
          <h3 className="text-sm font-semibold text-red-600 dark:text-red-400">
            Confirm {deleteTarget === 'collection' ? 'collection' : 'document'} deletion
          </h3>
          <p className={`mt-2 text-sm ${mutedClass}`}>
            This will permanently delete{' '}
            <span className="font-mono text-red-600 dark:text-red-400">{path}</span>
            {deleteTarget === 'document' ? ' and all nested subcollections.' : ' and every document inside it.'}
          </p>
          <label className={`block mt-4 text-sm ${mutedClass}`}>
            Type <span className="font-mono">{path}</span> to confirm:
          </label>
          <input
            type="text"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            className={`mt-1 block w-full rounded-lg border px-3 py-2 text-sm font-mono ${inputClass}`}
            placeholder={path}
          />
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              disabled={deleting || deleteConfirm.trim() !== path}
              onClick={handleDelete}
              className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button
              type="button"
              disabled={deleting}
              onClick={() => {
                setDeleteTarget(null);
                setDeleteConfirm('');
              }}
              className={`text-sm px-4 py-2 rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-300'}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFirebaseDatabase;
